import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth/auth.service';
import { LoaderService } from '../services/loader/loader.service';
import { NotificationService } from '../services/notification/notification.service';
import * as CoreActions from './actions';
import { IAppState } from './state';
import {
  selectPendingEncryptedUser,
  selectPendingIntermediateToken,
  selectPendingUserCode,
  selectPendingUsername,
} from './selectors';



/**
 * status 0 é o que o Angular reporta para falhas de rede/DNS/CORS (o request
 * nem chegou a um servidor) — comum neste esqueleto enquanto os domínios de
 * `environment.apiUrls` (src/environments/environment*.ts) não apontarem
 * para um backend real. Diferenciar isso de "credenciais inválidas" evita
 * confundir "não há backend" com "usuário/senha errados".
 */
function authErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'Não foi possível conectar ao servidor de autenticação. Verifique se o backend está no ar e se o domínio em environment.apiUrls.infraestrutura está correto.';
    }
    // 401 na própria tentativa de login/2FA é "credenciais/código inválidos"
    // — o corpo (quando existe) só traz um título genérico tipo "Unauthorized",
    // que é pior UX do que a mensagem de fallback já pensada pra isso.
    if (error.status === 401) {
      return fallback;
    }
    if (error.error?.message) {
      return error.error.message;
    }
    // A API real responde 400 no formato ValidationProblemDetails do .NET:
    // { title, errors: { Campo: ["mensagem"] } }. Ex.: recaptcha ausente,
    // usuário/senha vazios.
    const validationErrors = error.error?.errors;
    if (validationErrors && typeof validationErrors === 'object') {
      const firstMessage = Object.values(validationErrors).flat()[0];
      if (typeof firstMessage === 'string') {
        return firstMessage;
      }
    }
    if (error.error?.title) {
      return error.error.title;
    }
  }
  return fallback;
}



@Injectable()
export class CoreEffects {
  signInAttempt$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CoreActions.signInAttempt),
    tap(() => this.loader.show()),
    switchMap(({ credentials }) =>
      this.authService.signIn(credentials).pipe(
        map((response) =>
          CoreActions.setAuthData({
            token: response.token,
            user: response.usuario,
          })
        ),
        catchError((error) =>
          of(
            CoreActions.signInError({
              error: authErrorMessage(
                error,
                'Erro ao fazer acesso.'
              ),
            })
          )
        ),
        tap(() => this.loader.hide())
      )
    )
  )
);

//O BLOCO ABAIXO SERÁ REATIVADO QUANDO O BACKEND ESTIVER PRONTO PARA 2FA, POIS NO MOMENTO NÃO HÁ SUPORTE PARA ISSO NO BACKEND (BACKLOG)
  /*signInAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.signInAttempt),
      tap(() => this.loader.show()),
      switchMap(({ credentials }) =>
        this.authService.signIn(credentials).pipe(
          map(({ response, encryptedUser }) => {
            if (!response.twoFAEnabled) {
              // O app original também não trata esse caso como "logado direto"
              // — twoFAEnabled=false no retorno de Credentials significa que a
              // conta ainda precisa CONFIGURAR o 2FA (fluxo de QR code), que
              // este esqueleto não implementa.
              return CoreActions.signInError({
                error:
                  'Esta conta ainda não tem o segundo fator configurado (fluxo de configuração de QR code não implementado neste esqueleto).',
              });
            }
            if (environment.skipTwoFactorInDev) {
              // Conveniência só de frontend (environment.skipTwoFactorInDev) —
              // NÃO é um bypass de segurança real: o token abaixo é o
              // intermediário do backend, válido só para Auth/2FA/Validate.
              // Chamadas a outras APIs reais tendem a voltar 401 com ele.
              this.notification.warning(
                'MFA pulado (environment.skipTwoFactorInDev) — o token usado é apenas intermediário, chamadas a outras APIs podem falhar.',
                'Modo desenvolvimento'
              );
              return CoreActions.setAuthData({
                token: response.token,
                user: { 
                  id: '', 
                  username: credentials.usuarioLogin, 
                  nome: credentials.usuarioLogin, 



                  grupo: {
                    codigo: '',
                    nome: '',
                  },



                  departamento: {
                    codigo: '',
                    nome: '',
                  },
                  
                  menus: [],
                  tenants: [],
                },
              });
            }
            return CoreActions.signInTwoFactorRequired({
              encryptedUser,
              intermediateToken: response.token,
              userCode: response.userCode,
            });
          }),
          catchError((error) =>
            of(CoreActions.signInError({ error: authErrorMessage(error, 'Erro ao fazer acesso.') }))
          ),
          tap(() => this.loader.hide())
        )
      )
    )
  )*/



  validateTwoFactorAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.validateTwoFactorAttempt),
      withLatestFrom(
        this.store.select(selectPendingEncryptedUser),
        this.store.select(selectPendingIntermediateToken),
        this.store.select(selectPendingUserCode),
        this.store.select(selectPendingUsername)
      ),
      tap(() => this.loader.show()),
      switchMap(([{ codigo }, encryptedUser, intermediateToken, userCode, pendingUsername]) =>
        this.authService
          .validateTwoFactor({
            codigo,
            encryptedUser: encryptedUser ?? '',
            intermediateToken: intermediateToken ?? '',
            userCode: userCode ?? undefined,
          })
          .pipe(
            map((response) =>
              CoreActions.setAuthData({
                token: response.token,
                // O backend não retorna um objeto de usuário/perfil — só o
                // token. Construímos um User mínimo com o que já temos.
                user: {
                  id: '',
                  username: pendingUsername ?? '',
                  nome: pendingUsername ?? '',
                  
                  grupo: {
                    codigo: '',
                    nome: '',
                  },



                  departamento: {
                    codigo: '',
                    nome: '',
                  },
                  menus: [],
                  tenants: [],
                },
              })
            ),
            catchError((error) =>
              // Confirmado no bundle real: toastrService.error("Código inválido.", "Erro")
              of(CoreActions.signInError({ error: authErrorMessage(error, 'Código inválido.') }))
            ),
            tap(() => this.loader.hide())
          )
      )
    )
  );



  signInSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.setAuthData),
        tap(() => this.router.navigate(['/home']))
      ),
    { dispatch: false }
  );



  signInError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.signInError),
        // Réplica do app original: só um toast (título "Erro"), sem texto
        // fixo na tela — confirmado observando o comportamento real ao
        // digitar um código de 2FA errado.
        tap(({ error }) => this.notification.error(error, 'Erro'))
      ),
    { dispatch: false }
  );



  signOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.signOut),
        tap(() => this.router.navigate(['/login']))
      ),
    { dispatch: false }
  );



  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private loader: LoaderService,
    private notification: NotificationService,
    private router: Router,
    private store: Store<IAppState>
  ) {}
}